import React, { lazy } from 'react';
import { Switch } from 'react-router-dom';

import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import { roles, userAllowed } from '../helpers/utils';
import { REPORT_NAVIGATION } from '../helpers/reportUtils';
import { performanceLicensing } from '../helpers/performanceUtil';

const LazyLoginPage = lazy(() => import('../components/LoginPage'));
const LazyLogoutPage = lazy(() => import('../components/LogoutPage'));

const LazyUnifiedDashboardPage = lazy(() => import('../components/dashboard/UnifiedDashboardPage'));

const LazySimsPage = lazy(() => import('../components/sims/manage-sims/SimsPage'));
const LazyESimsPage = lazy(() => import('../components/sims/esims/ESimsPage'));
const LazySimAddPage = lazy(() => import('../components/sims/add-sims/SimAddPage'));

const LazyUsersPage = lazy(() => import('../components/access-and-permissions/users/UsersPage'));
const LazyUserManagePage = lazy(() => import('../components/access-and-permissions/users/UserManagePage'));

const LazyRolesPage = lazy(() => import('../components/access-and-permissions/roles/RolesPage'));

const LazyNetworkNavigatorPage = lazy(() => import('../components/network-navigator/NetworkNavigator'));
const LazyMultiSiteNetworkNavigatorPage = lazy(() => import('../components/network-navigator/multi-site/MultiSiteNetworkNavigator'));

const LazyFloorPlansTabPage = lazy(() => import('../components/floor-plans/FloorPlanTab'));

const LazyDevicesPage = lazy(() => import('../components/device-monitoring/DevicesPage'));

const LazyTicketingPage = lazy(() => import('../components/ticketing/TicketingPage'));
const LazyPwnTrackerPage = lazy(() => import('../components/enablers/pwn-tracker/PwnTrackerPage'));
const LazyTicketingCreatePage = lazy(() => import('../components/ticketing/TicketingCreatePage'));

const LazyDirectorySyncPage = lazy(() => import('../components/sims/directory-sync/DirectorySyncPage'));
const LazyBackupsPage = lazy(() => import('../components/sims/backup/BackupsPage'));
const LazyAuditsPage = lazy(() => import('../components/sims/audit/AuditsPage'));
const LazyRestorePage = lazy(() => import('../components/sims/restore/RestorePage'));

// Performance
const LazyPerformancePage = lazy(() => import('../components/kpis/PerformancePage'));
const LazyPerformanceCorePageWrapper = lazy(() => import('../components/kpis/PerformanceCorePageWrapper'));
const LazyPerformanceRadioPageWrapper = lazy(() => import('../components/kpis/PerformanceRadioPageWrapper'));
const LazyPerformanceUsagePage = lazy(() => import('../components/kpis/PerformanceUsagePage'));

const LazyPerformanceExfoPage = lazy(() => import('../components/kpis-exfo/PerformancePage'));
const LazyPerformanceCoreExfoPage = lazy(() => import('../components/kpis-exfo/PerformanceCorePage'));
const LazyPerformanceRadioExfoPage = lazy(() => import('../components/kpis-exfo/PerformanceRadioPage'));
const LazyPerformanceFlsPage = lazy(() => import('../components/kpis-fls/PerformanceFlsPage'));
const LazyPerformanceRadioFlsPage = lazy(() => import('../components/kpis-fls/PerformanceRadioFlsPage'));
const LazyPerformanceCoreFlsPage = lazy(() => import('../components/kpis-fls/PerformanceCoreFlsPage'));
const LazyPerformanceUsageFlsPage = lazy(() => import('../components/kpis-fls/PerformanceUsageFlsPage'));

// Multi-NIB
const LazyMultiPerformancePage = lazy(() => import('../components/kpis/multi-nib/overview/MultiPerformancePage'));

//MEC
const LazyMECPerformancePage = lazy(() => import('../components/mec/PerformancePage'));
const LazyMECPerformanceOSNDPage = lazy(() => import('../components/mec/osnd/PerformancePage'));
const LazyUsageReportPage = lazy(() => import('../components/mec/UsageReportPage'));

// Alerts
const LazyAlertsPage = lazy(() => import('../components/alerts/list/AlertsPage'));
const LazyAlertDownloads = lazy(() => import('../components/alerts/AlertDownloads'));
// const LazyAlertRules = lazy(() => import('../components/alerts/AlertRules'));

const LazyAlertRules = lazy(() => import('../components/alerts/rules/RulesPage'));

// Audit Logs
const LazyAuditLogsPage = lazy(() => import('../components/audit-logs/AuditLogsPage'));

// Reporting
const LazyReportingPage = lazy(() => import('../components/reporting/ReportingPage'));
const LazyResponseNotificationTimePage = lazy(() => import('../components/reporting/response-notification-time/ResponseNotificationTimePage'));
const LazyAvailabilityTimePage = lazy(() => import('../components/reporting/availability/AvailabilityPage'));

const LazyNotFoundPage = lazy(() => import('../components/access-denied/NotFoundPage'));

const LazyErrorPage = lazy(() => import('../components/ErrorPage'));

const LazyHelpPage = lazy(() => import('../components/HelpPage'));
const LazyPortalPage = lazy(() => import('../components/PortalPage'));
const LazyVideoGuidePage = lazy(() => import('../components/VideoGuidePage'));
const LazyJourneyGuidePage = lazy(() => import('../components/JourneyGuidePage'));

const AppRouter = () => (
    <Switch>
        {/* LEFT As Example.... <DualRoute path="/" publicComponent={LazyRegistrationPage} privateComponent={LazyDashboardPage} requiredRole={USER_AND_ADMIN} exact /> */}

        <PrivateRoute path="/" component={LazyUnifiedDashboardPage} exact requiredRole={[]} subNav="dashboard" />

        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/users/roles/:pageNum?"
            component={LazyRolesPage}
            exact
            requiredRole={roles.VIEW_ROLES}
            hero="roles"
            subNav="users"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/users/add"
            component={LazyUserManagePage}
            exact
            requiredRole={roles.MANAGE_USERS}
            subNav="users"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/users/:userId([a-z0-9]{24})"
            component={LazyUserManagePage}
            exact
            requiredRole={roles.VIEW_USERS}
            subNav="users"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/users/:pageNum?"
            component={LazyUsersPage}
            exact
            requiredRole={roles.VIEW_USERS}
            hero="users"
            subNav="users"
        />
        <PrivateRoute
            path="/sims/directory-sync"
            component={LazyDirectorySyncPage}
            exact
            requiredRole={[roles.VIEW_DIRECTORY_SYNC_SCIM, roles.VIEW_SIM_MANAGEMENT]}
            subNav="sims"
        />
        <PrivateRoute
            path="/sims/esims/:pageNum?"
            component={LazyESimsPage}
            exact
            requiredRole={[roles.VIEW_ESIM_SUPPORT, roles.VIEW_SIM_MANAGEMENT]}
            subNav="sims"
        />
        <PrivateRoute
            path="/sims/backup/:pageNum?"
            component={LazyBackupsPage}
            exact
            requiredRole={[roles.VIEW_BACKUPS, roles.VIEW_SIM_MANAGEMENT]}
            subNav="sims"
        />
        <PrivateRoute
            path="/sims/audit/:pageNum?"
            component={LazyAuditsPage}
            exact
            requiredRole={[roles.VIEW_AUDIT_RESOLVE, roles.VIEW_SIM_MANAGEMENT]}
            subNav="sims"
        />
        <PrivateRoute
            path="/sims/restore/:pageNum?"
            component={LazyRestorePage}
            exact
            requiredRole={[roles.VIEW_AUDIT_RESOLVE, roles.VIEW_SIM_MANAGEMENT]}
            subNav="sims"
        />
        <PrivateRoute
            path="/sims/add/bulk"
            component={LazySimAddPage}
            exact
            requiredRole={[roles.MANAGE_SUBSCRIBERS, roles.VIEW_SIM_MANAGEMENT]}
            subNav="sims"
        />
        <PrivateRoute
            path="/sims/add/single"
            component={LazySimAddPage}
            exact
            requiredRole={[roles.MANAGE_SUBSCRIBERS, roles.VIEW_SIM_MANAGEMENT]}
            subNav="sims"
        />
        <PrivateRoute
            path="/sims/:pageNum?"
            component={LazySimsPage}
            exact
            requiredRole={[roles.VIEW_SUBSCRIBERS, roles.VIEW_SIM_MANAGEMENT]}
            hero="sims"
            subNav="sims"
        />

        <PrivateRoute
            path="/monitoring/inventory/:nodeKey?"
            component={LazyNetworkNavigatorPage}
            requiredRole={[roles.VIEW_INVENTORY]}
            hero="monitoring"
            subNav="monitoring"
        />
        <PrivateRoute
            path="/monitoring/multi-site-inventory/:nodeKey?"
            component={LazyMultiSiteNetworkNavigatorPage}
            requiredRole={[roles.MULTI_SITE_INVENTORY, roles.MANAGE_MULTI_NIB_COMBINED_VIEW]}
            licenseCheck={({ userRoles }) => userAllowed(userRoles, [roles.MULTI_SITE_INVENTORY, roles.MANAGE_MULTI_NIB_COMBINED_VIEW], false)}
            hero="monitoring"
            subNav="monitoring"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/monitoring/multi-site-performance"
            component={LazyMultiPerformancePage}
            requiredRole={[roles.MULTI_SITE_PERFORMANCE, roles.MANAGE_MULTI_NIB_COMBINED_VIEW]}
            licenseCheck={({ userRoles }) => userAllowed(userRoles, [roles.MULTI_SITE_PERFORMANCE, roles.MANAGE_MULTI_NIB_COMBINED_VIEW], false)}
            hero="monitoring"
            subNav="monitoring"
        />
        <PrivateRoute
            path="/monitoring/floor-plans-tab/:fileLocation"
            component={LazyFloorPlansTabPage}
            requiredRole={[roles.MANAGE_FLOOR_PLANS]}
            hero="monitoring"
            subNav="monitoring"
        />
        <PrivateRoute
            path="/monitoring/devices/:pageNum?"
            component={LazyDevicesPage}
            exact
            requiredRole={roles.VIEW_DEVICE_MONITORING}
            licenseCheck={({ userRoles }) => userAllowed(userRoles, [roles.MANAGE_DEVICES])}
            hero="monitoring"
            subNav="monitoring"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/monitoring/performance"
            component={LazyPerformancePage}
            exact
            requiredRole={[roles.VIEW_NETWORK_PERFORMANCE]}
            licenseCheck={({ userRoles }) => userAllowed(userRoles, [roles.VIEW_PERFORMANCE_PAGE, roles.VIEW_NEW_PERFORMANCE_PAGE], true)}
            hero="monitoring"
            subNav="monitoring"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/monitoring/performance/core"
            component={LazyPerformanceCorePageWrapper}
            exact
            requiredRole={[roles.VIEW_NETWORK_PERFORMANCE]}
            licenseCheck={({ userRoles, solutionType }) =>
                userAllowed(userRoles, [roles.VIEW_PERFORMANCE_PAGE, roles.VIEW_NEW_PERFORMANCE_PAGE], true) &&
                performanceLicensing('coreNetwork', userRoles, solutionType)
            }
            hero="monitoring"
            subNav="monitoring"
        />

        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/monitoring/performance/radio"
            component={LazyPerformanceRadioPageWrapper}
            exact
            requiredRole={[roles.VIEW_NETWORK_PERFORMANCE]}
            licenseCheck={({ userRoles, solutionType }) =>
                userAllowed(userRoles, [roles.VIEW_PERFORMANCE_PAGE, roles.VIEW_NEW_PERFORMANCE_PAGE], true) &&
                performanceLicensing('radioNetwork', userRoles, solutionType)
            }
            hero="monitoring"
            subNav="monitoring"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/monitoring/performance/usage"
            component={LazyPerformanceUsagePage}
            exact
            requiredRole={[roles.VIEW_NETWORK_PERFORMANCE]}
            licenseCheck={({ userRoles, solutionType }) =>
                userAllowed(userRoles, [roles.VIEW_PERFORMANCE_PAGE, roles.VIEW_NEW_PERFORMANCE_PAGE], true) &&
                performanceLicensing('usage', userRoles, solutionType)
            }
            hero="monitoring"
            subNav="monitoring"
        />

        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/monitoring/performance-exfo"
            component={LazyPerformanceExfoPage}
            exact
            requiredRole={roles.VIEW_KPI_EXFO}
            hero="monitoring"
            subNav="monitoring"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/monitoring/performance-exfo/radio-exfo"
            component={LazyPerformanceRadioExfoPage}
            exact
            requiredRole={roles.VIEW_KPI_EXFO}
            hero="monitoring"
            subNav="monitoring"
        />

        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/monitoring/performance-exfo/core-exfo"
            component={LazyPerformanceCoreExfoPage}
            exact
            requiredRole={roles.VIEW_KPI_EXFO}
            hero="monitoring"
            subNav="monitoring"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/monitoring/performance-fls"
            component={LazyPerformanceFlsPage}
            exact
            requiredRole={[roles.MANAGE_FLS_KPI_INTEGRATION]}
            hero="monitoring"
            subNav="monitoring"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/monitoring/performance-fls/radio"
            component={LazyPerformanceRadioFlsPage}
            exact
            requiredRole={roles.MANAGE_FLS_KPI_INTEGRATION}
            hero="monitoring"
            subNav="monitoring"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/monitoring/performance-fls/core"
            component={LazyPerformanceCoreFlsPage}
            exact
            requiredRole={roles.MANAGE_FLS_KPI_INTEGRATION}
            hero="monitoring"
            subNav="monitoring"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/monitoring/performance-fls/usage"
            component={LazyPerformanceUsageFlsPage}
            exact
            requiredRole={roles.MANAGE_FLS_KPI_INTEGRATION}
            hero="monitoring"
            subNav="monitoring"
        />
        {/* MEC menu */}
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/mec/performance"
            component={LazyMECPerformancePage}
            exact
            requiredRole={roles.VIEW_MEC_PERFORMANCE}
            licenseCheck={({ userRoles }) => userAllowed(userRoles, [roles.MANAGE_MEC_PERFORMANCE])}
            hero="mec"
            subNav="mec"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/mec/performance-osnd"
            component={LazyMECPerformanceOSNDPage}
            exact
            requiredRole={roles.MANAGE_MEC_PERFORMANCE_OSND}
            hero="mec"
            subNav="mec"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/mec/usage-reports"
            component={LazyUsageReportPage}
            exact
            requiredRole={roles.VIEW_MEC_REPORTS}
            licenseCheck={({ userRoles }) => userAllowed(userRoles, [roles.MANAGE_MEC_USAGE_REPORTS])}
            hero="mec"
            subNav="mec"
        />

        {/* Enablers menu */}
        <PrivateRoute path="/:enterpriseId([a-z0-9]{24})?/pwn-tracker" component={LazyPwnTrackerPage} exact requiredRole={[]} />

        {/* Alerts menu start */}
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/support/alerts"
            component={LazyAlertsPage}
            exact
            requiredRole={roles.VIEW_MANAGE_ALERTS}
            licenseCheck={({ userRoles }) => userAllowed(userRoles, [roles.MANAGE_ALERTS])}
            hero="support"
            subNav="support"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/support/alerts/downloads"
            component={LazyAlertDownloads}
            exact
            requiredRole={roles.VIEW_MANAGE_ALERTS}
            licenseCheck={({ userRoles }) => userAllowed(userRoles, [roles.MANAGE_ALERTS])}
            hero="support"
            subNav="support"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/support/alerts/rules"
            component={LazyAlertRules}
            exact
            requiredRole={roles.VIEW_MANAGE_ALERTS}
            licenseCheck={({ userRoles }) => userAllowed(userRoles, [roles.MANAGE_ALERTS])}
            hero="support"
            subNav="support"
        />
        <PrivateRoute
            path="/:enterpriseId([a-z0-9]{24})?/support/logs"
            component={LazyAuditLogsPage}
            exact
            requiredRole={roles.VIEW_AUDIT_LOGS}
            hero="support"
            subNav="support"
        />

        {/* Reporting menu start */}
        <PrivateRoute
            path={`/:enterpriseId([a-z0-9]{24})?${REPORT_NAVIGATION.RESTORATION_TIME}/:pageNum([0-9]+)?`}
            component={LazyReportingPage}
            exact
            requiredRole={roles.MANAGE_SERVICE_LEVEL_RESORATION_TIME}
            hero="monitoring"
            subNav="monitoring"
        />
        <PrivateRoute
            path={`/:enterpriseId([a-z0-9]{24})?${REPORT_NAVIGATION.RESPONSE_NOTIFICATION_TIME}/:pageNum([0-9]+)?`}
            component={LazyResponseNotificationTimePage}
            exact
            requiredRole={roles.MANAGE_SERVICE_LEVEL_RESPONSE_NOTIF_TIME}
            hero="monitoring"
            subNav="monitoring"
        />
        <PrivateRoute
            path={`/:enterpriseId([a-z0-9]{24})?${REPORT_NAVIGATION.AVAILABILITY}/:pageNum([0-9]+)?`}
            component={LazyAvailabilityTimePage}
            exact
            requiredRole={[roles.MANAGE_SERVICE_LEVEL_AVAILABILITY, roles.MANAGE_SERVICE_LEVEL_AVAILABILITY_PLUS]}
            hero="monitoring"
            subNav="monitoring"
        />

        <PrivateRoute
            path="/maintenance/ticketing/create"
            component={LazyTicketingCreatePage}
            exact
            requiredRole={roles.VIEW_TICKETING}
            hero="maintenance"
            subNav="maintenance"
        />

        <PrivateRoute
            path="/maintenance/ticketing/:pageNum([0-9]+)?"
            component={LazyTicketingPage}
            exact
            requiredRole={roles.VIEW_TICKETING}
            hero="maintenance"
            subNav="maintenance"
        />

        <PublicRoute path="/help" component={LazyHelpPage} exact />
        <PublicRoute path="/video/guide" component={LazyVideoGuidePage} exact />
        <PublicRoute path="/portal" component={LazyPortalPage} exact />
        <PublicRoute path="/journey/guide" component={LazyJourneyGuidePage} exact />

        <PublicRoute path="/login" component={LazyLoginPage} showNav={false} />
        <PublicRoute path="/logout" component={LazyLogoutPage} exact showNav={false} />

        <PublicRoute path="/error" component={LazyErrorPage} showNav={false} />

        <PublicRoute component={LazyNotFoundPage} />
    </Switch>
);

export default AppRouter;
